import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Globe, MessageCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Shows where the family's supporter base is coming from — by source.
 *  Helps the admin understand at a glance whether momentum is via in-app signups,
 *  externally-tracked Wycliffe gifts, or CRM conversations promoted to pledges. */
export const SourceBreakdown = () => {
  const [data, setData] = useState({ in_app: 0, external: 0, conversation: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/supporters-unified`, { withCredentials: true })
      .then(({ data: rows }) => {
        const counts = { in_app: 0, external: 0, conversation: 0, total: rows.length };
        rows.forEach(r => { if (counts[r.source] !== undefined) counts[r.source]++; });
        setData(counts);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || data.total === 0) return null;

  const segments = [
    { key: 'in_app',       label: 'In-app pledges',          count: data.in_app,       color: 'bg-blue-500',   tint: 'bg-blue-50 text-blue-700',     Icon: Users },
    { key: 'external',     label: 'External (Wycliffe)',     count: data.external,     color: 'bg-amber-500',  tint: 'bg-amber-50 text-amber-700',   Icon: Globe },
    { key: 'conversation', label: 'From CRM conversations',  count: data.conversation, color: 'bg-violet-500', tint: 'bg-violet-50 text-violet-700', Icon: MessageCircle },
  ];

  return (
    <div data-testid="source-breakdown" className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
      <h3 className="text-base font-bold text-slate-900 mb-1 font-['Outfit']">Supporters by source</h3>
      <p className="text-xs text-slate-500 mb-4">
        Where your {data.total} {data.total === 1 ? 'supporter is' : 'supporters are'} coming from.
      </p>
      {/* Single stacked bar */}
      <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-slate-100 mb-4">
        {segments.map(s => (
          <div
            key={s.key}
            className={`${s.color} transition-all`}
            style={{ width: `${data.total ? (s.count / data.total) * 100 : 0}%` }}
            title={`${s.label}: ${s.count}`}
          />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {segments.map(s => {
          const Icon = s.Icon;
          const pct = data.total ? Math.round((s.count / data.total) * 100) : 0;
          return (
            <div key={s.key} className={`rounded-xl px-3 py-2.5 ${s.tint}`}>
              <div className="flex items-center gap-1.5 text-xs font-semibold">
                <Icon className="w-3.5 h-3.5" />
                <span>{s.label}</span>
              </div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{s.count}</div>
              <div className="text-xs text-slate-500">{pct}%</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SourceBreakdown;
